import { useMutation } from '@apollo/client';
import {
  ALL_MODULES,
  EventEmitter,
  getCurrentUserHook,
  getTranslatedText,
  toast,
  useActivityTracker,
  useIntl,
  withErrorBoundary
} from '@nile/components';
import { ADD_NOTE_WORKITEM, GET_TICKET_AUDIT_LIST } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { ActionContainerV2 } from '@nile/mangifera/src/Containers/ChatContainers';
import { isEmpty } from 'lodash';
import React from 'react';

const NoteEditorContainer = (props) => {
  const {
    ticket,
    resetConversationMode,
    conversationOrder,
    focusLastAddedMessage,
    prompts,
    sendAIFeatureFeedback
  } = props;
  const intl = useIntl();

  const actionContainerRef = React.useRef(null);

  const DEFAULT_TEMPLATE = {
    name: '',
    content: '',
    placeholdersUsed: '',
    mentions: [],
    attachments: [],
    inlineImages: []
  };

  const editorConfig = {
    id: ticket?.ticketId,
    name: `note${ticket?.ticketId}`,
    cacheConfig: {
      scope: 'global'
    }
  };

  const currentUser = getCurrentUserHook();

  const updateEditorContent = (content) => {
    actionContainerRef.current?.updateTemplate(content);
    EventEmitter.dispatch('replaceEditorContent', content?.content);
  };

  const [
    addNote,
    { loading: newNoteLoading, error: newNoteError }
  ] = useMutation(ADD_NOTE_WORKITEM, {
    update(cache, { data }) {
      const addedNote = data?.createNote;
      if (!addedNote) return;

      const newFeed = {
        __typename: 'TicketAudit',
        id: addedNote.noteId,
        type: 'NOTE',
        time: addedNote.addedOn ?? addedNote.time,

        user: {
          userId: currentUser?.userId,
          name: currentUser?.name,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          email: currentUser?.email
        },

        conversation: null,
        threadConversation: null,
        conversationThread: null,
        vendorConversation: null,

        note: {
          __typename: 'Note',
          noteId: addedNote.noteId,
          content: addedNote.content,
          addedOn: addedNote.addedOn,
          addedBy: addedNote.addedBy,
          attachments: addedNote.attachments ?? [],
          privacyType: addedNote.privacyType,
          workItem: addedNote.workItem
        },

        approval: null,
        task: null
      };

      cache.modify({
        fields: {
          getTicketAuditList(existing = {}, { readField }) {
            const existingFeeds = existing.ticketAudit ?? [];

            // prevent duplicates (important for optimistic / retries)
            const alreadyExists = existingFeeds.some(
              (ref) => readField('id', ref) === newFeed.id
            );
            if (alreadyExists) return existing;

            return {
              ...existing,
              ticketAudit:
                conversationOrder === 'DESC'
                  ? [newFeed, ...existingFeeds]
                  : [...existingFeeds, newFeed]
            };
          }
        }
      });

      if (!props?.mode) {
        resetConversationMode();
      }
    },
    onCompleted() {
      toast('success', 'New note created successfully');
      useActivityTracker({ event: 'Ticket - Added internal note' });
      focusLastAddedMessage();
      updateEditorContent(DEFAULT_TEMPLATE);
      actionContainerRef.current?.updateState('disabled', false);
      actionContainerRef.current?.updateState('submitActionLoading', false);
    }
  });

  const onNoteSubmit = (params, callback) => {
    const workItem = {
      workId: ticket?.ticketId,
      module: ALL_MODULES.ticket
    };

    const attachments = !isEmpty(params?.attachments)
      ? params?.attachments.map(({ downloadURL, ...keepAttrs }) => keepAttrs)
      : [];

    const variables = {
      input: {
        content: params?.message,
        mentions: params?.mentions,
        workItem: workItem,
        addAttachments: attachments,
        privacyType: params?.privacyType,
        inlineImages: params?.inlineImages || []
      }
    };
    addNote({ variables: variables });
  };

  return (
    <div className="m15">
      <ActionContainerV2
        enablePublic
        hideRecipients
        parentRef={actionContainerRef}
        ticket={ticket}
        convertConversationToWorklog
        mode="note"
        placeholder={getTranslatedText(intl, 'ticket.details.chat.pl.desc')}
        onMessageSubmit={onNoteSubmit}
        module="NOTE"
        details={{
          workItem: {
            module: 'TICKET',
            workId: ticket.ticketId
          }
        }}
        attachmentUploadUrl={`${envConfig.fileRoot}/upload`}
        uploadParams={{
          module: ALL_MODULES.ticketNoteAttachment,
          details: JSON.stringify({
            ticketId: ticket.ticketId
          })
        }}
        allowMentions
        metadata={{ module: 'TICKET_NOTE', data: ticket }}
        prompts={prompts}
        showAIRephrase
        sendAIFeatureFeedback={sendAIFeatureFeedback}
        actionButtonStyles={{
          position: 'relative',
          right: '0',
          bottom: '0'
        }}
        onCancel={resetConversationMode}
        editorConfig={editorConfig}
      />
    </div>
  );
};

export default withErrorBoundary(NoteEditorContainer);
